import { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";

type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

/**
 * Swaps the active <link rel="manifest"> based on the current route so that
 * users can install the main site, admin panel, or PDV as separate apps.
 */
export const usePWAManifest = () => {
  const { pathname } = useLocation();
  const [deferredPrompt, setDeferredPrompt] = useState<BIPEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);

  useEffect(() => {
    let manifest = "/manifest.json";
    let appName = "Mente Sã";
    if (pathname.startsWith("/admin")) {
      manifest = "/manifest-admin.json";
      appName = "Mente Sã | Admin";
    } else if (pathname.startsWith("/pdv")) {
      manifest = "/manifest-pdv.json";
      appName = "Mente Sã | PDV";
    }

    let link = document.querySelector<HTMLLinkElement>("link[rel='manifest']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "manifest";
      document.head.appendChild(link);
    }
    if (link.getAttribute("href") !== manifest) {
      link.setAttribute("href", manifest);
    }

    let appleTitle = document.querySelector<HTMLMetaElement>(
      "meta[name='apple-mobile-web-app-title']"
    );
    if (!appleTitle) {
      appleTitle = document.createElement("meta");
      appleTitle.name = "apple-mobile-web-app-title";
      document.head.appendChild(appleTitle);
    }
    appleTitle.content = appName;
  }, [pathname]);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BIPEvent);
      setCanInstall(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return false;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setCanInstall(false);
    return outcome === "accepted";
  }, [deferredPrompt]);

  return { canInstall, promptInstall };
};
